import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Plus as PlusIcon, Trash2 as TrashIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useProgressStore } from '../store/progressStore';
import { useWorkoutStore } from '../store/workoutStore';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { StatCard } from '../components/Cards';
import LoadProgression from '../components/LoadProgression';

const Progress = () => {
  const measurements = useProgressStore((state) => state.measurements);
  const addMeasurement = useProgressStore((state) => state.addMeasurement);
  const deleteMeasurement = useProgressStore((state) => state.deleteMeasurement);
  const streak = useProgressStore((state) => state.workoutStreak);
  const history = useWorkoutStore((state) => state.history);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    weight: '',
    chest: '',
    waist: '',
    arms: '',
    legs: '',
    notes: '',
  });

  const handleAddMeasurement = () => {
    if (formData.weight || formData.chest || formData.waist || formData.arms || formData.legs) {
      addMeasurement({
        weight: formData.weight ? Number(formData.weight) : null,
        chest: formData.chest ? Number(formData.chest) : null,
        waist: formData.waist ? Number(formData.waist) : null,
        arms: formData.arms ? Number(formData.arms) : null,
        legs: formData.legs ? Number(formData.legs) : null,
        notes: formData.notes,
      });
      setFormData({ weight: '', chest: '', waist: '', arms: '', legs: '', notes: '' });
      setDialogOpen(false);
    }
  };

  // Datos para gráficos
  const weightData = measurements
    .filter((m) => m.weight)
    .map((m) => ({
      date: format(new Date(m.date), 'dd MMM', { locale: es }),
      weight: m.weight,
    }))
    .reverse();

  const volumeByMonth = {};
  history.forEach((workout) => {
    const month = format(new Date(workout.date), 'MMM', { locale: es });
    if (!volumeByMonth[month]) volumeByMonth[month] = 0;
    workout.exercises.forEach((ex) => {
      ex.sets.forEach((set) => {
        volumeByMonth[month] += set.weight * set.reps;
      });
    });
  });

  const volumeData = Object.entries(volumeByMonth).map(([month, volume]) => ({
    month,
    volume: Math.round(volume),
  }));

  const latestMeasurement = measurements.length > 0 ? measurements[0] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Mi Progreso
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlusIcon size={18} />}
          onClick={() => setDialogOpen(true)}
        >
          Nueva Medida
        </Button>
      </Box>

      <LoadProgression />

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="🎯"
            label="Streak"
            value={`${streak}d`}
            variant="warning"
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="📊"
            label="Medidas"
            value={measurements.length}
            variant="info"
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="🏋️"
            label="Entrenamientos"
            value={history.length}
            variant="success"
          />
        </Grid>
      </Grid>

      {/* Weight Chart */}
      {weightData.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Peso
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={weightData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="weight" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
            {latestMeasurement && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Peso actual
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {latestMeasurement.weight} kg
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Volume Chart */}
      {volumeData.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Volumen Mensual
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="volume" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Measurements */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Historial de Medidas
          </Typography>
          {measurements.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sin medidas registradas
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {measurements.map((m, idx) => (
                <Box
                  key={m.id}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {format(new Date(m.date), 'dd MMMM yyyy', { locale: es })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {m.weight && (
                        <Typography variant="caption">
                          ⚖️ {m.weight}kg
                        </Typography>
                      )}
                      {m.chest && (
                        <Typography variant="caption">
                          🫀 {m.chest}cm
                        </Typography>
                      )}
                      {m.waist && (
                        <Typography variant="caption">
                          👖 {m.waist}cm
                        </Typography>
                      )}
                      {m.arms && (
                        <Typography variant="caption">
                          💪 {m.arms}cm
                        </Typography>
                      )}
                      {m.legs && (
                        <Typography variant="caption">
                          🦵 {m.legs}cm
                        </Typography>
                      )}
                    </Box>
                    {m.notes && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {m.notes}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    component="button"
                    onClick={() => deleteMeasurement(m.id)}
                    sx={{
                      bg: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { opacity: 0.7 },
                    }}
                  >
                    <TrashIcon size={18} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Medidas</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'grid', gap: 1.5 }}>
          <TextField
            fullWidth
            label="Peso (kg)"
            type="number"
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
          />
          <TextField
            fullWidth
            label="Pecho (cm)"
            type="number"
            value={formData.chest}
            onChange={(e) => setFormData({ ...formData, chest: e.target.value })}
          />
          <TextField
            fullWidth
            label="Cintura (cm)"
            type="number"
            value={formData.waist}
            onChange={(e) => setFormData({ ...formData, waist: e.target.value })}
          />
          <TextField
            fullWidth
            label="Brazos (cm)"
            type="number"
            value={formData.arms}
            onChange={(e) => setFormData({ ...formData, arms: e.target.value })}
          />
          <TextField
            fullWidth
            label="Piernas (cm)"
            type="number"
            value={formData.legs}
            onChange={(e) => setFormData({ ...formData, legs: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notas (opcional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddMeasurement} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Progress;
